import { cloneDeep } from 'lodash';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { assertNever } from '../../util';

import {
  Character,
  CharacterOrientation,
  UninitializedCharacter,
} from './types';
import { CHAR_MIN_SIZE, initializeCharacters } from './utils';

export enum ViewMode {
  READ_ONLY = 'READ_ONLY',
  EDIT = 'EDIT',
}

export enum DisplayMode {
  NORMAL = 'NORMAL',
  FULLSCREEN = 'FULLSCREEN',
}

interface SceneCanvasContextProps {
  viewMode: ViewMode;
  displayMode: DisplayMode;
  toggleDisplayMode: () => void;

  characters: Character[];
  getCharacterById: (characterId: Character['id']) => Character;
  scaleCharacter: (
    characterId: Character['id'],
    newX: number | null,
    newY: number | null,
    newWidth: number | null,
    newHeight: number | null
  ) => void;
  flipCharacter: (characterId: string) => void;

  characterInFocus: Character['id'] | null;
  setCharacterInFocus: (characterId: Character['id'] | null) => void;
  clearCharacterFocus: () => void;

  svg: {
    svgContainerRef: React.RefObject<SVGSVGElement> | null;
    getContainerCTM: () => DOMMatrix;
    setContainerCTM: Dispatch<SetStateAction<DOMMatrix | null>>;
  };
  children?: React.ReactNode;
}

const initialSceneCanvasContext: SceneCanvasContextProps = {
  viewMode: ViewMode.EDIT,
  displayMode: DisplayMode.NORMAL,
  toggleDisplayMode: () => {
    throw new Error('NOT IMPLEMENTED');
  },

  characters: [],
  getCharacterById: () => {
    throw new Error('NOT IMPLEMENTED');
  },
  scaleCharacter: () => {
    throw new Error('NOT IMPLEMENTED');
  },
  flipCharacter: () => {
    throw new Error('NOT IMPLEMENTED');
  },

  characterInFocus: 'none',
  setCharacterInFocus: () => {
    throw new Error('NOT IMPLEMENTED');
  },
  clearCharacterFocus: () => {
    throw new Error('NOT IMPLEMENTED');
  },

  svg: {
    svgContainerRef: null,
    getContainerCTM: () => {
      throw new Error('NOT IMPLEMENTED');
    },
    setContainerCTM: () => {
      throw new Error('NOT IMPLEMENTED');
    },
  },
  children: undefined,
};

export const SceneCanvasContext = React.createContext(
  initialSceneCanvasContext
);

export const SceneCanvasContextProvider: React.FC<{
  viewMode: ViewMode;
  initialCharacters: UninitializedCharacter[];
  children: React.ReactNode;
}> = ({ children, viewMode, initialCharacters }) => {
  //////////////////
  // Display Mode //
  //////////////////
  const [displayMode, setDisplayMode] = useState(DisplayMode.NORMAL);

  const toggleDisplayMode = useCallback(() => {
    switch (displayMode) {
      case DisplayMode.NORMAL:
        setDisplayMode(DisplayMode.FULLSCREEN);
        return;
      case DisplayMode.FULLSCREEN:
        setDisplayMode(DisplayMode.NORMAL);
        return;
      default:
        assertNever(displayMode);
    }
  }, [displayMode]);

  ////////////////
  // Characters //
  ////////////////
  const [characters, setCharacters] = useState<Character[]>([]);

  useEffect(() => {
    const setChars = async () => {
      setCharacters(await initializeCharacters(initialCharacters));
    };
    setChars();
  }, [initialCharacters]);

  const getCharacterById = (charId: Character['id']) => {
    const char = characters.find((char) => char.id === charId);
    if (!char) {
      throw new Error(`Unable to get character with id ${charId}!`);
    }
    return char;
  };

  const updateCharacter = (char: Character) => {
    const indexOfCharacter = characters.findIndex(
      (currentChar) => currentChar.id === char.id
    );

    const updatedCharacters = cloneDeep(characters);
    updatedCharacters[indexOfCharacter] = cloneDeep(char);

    const sortedCharacters = putCharacterOnTop(updatedCharacters, char.id);

    setCharacters(sortedCharacters);
  };

  const putCharacterOnTop = (characters: Character[], characterId: string) => {
    const newlySortedcharacters = cloneDeep(characters).sort((charX, charY) => {
      if (charX.id === characterId) {
        return 1;
      }
      if (charY.id === characterId) {
        return -1;
      }
      return 0;
    });

    return newlySortedcharacters;
  };

  const scaleCharacter = (
    charId: string,
    newX: number | null,
    newY: number | null,
    newWidth: number | null,
    newHeight: number | null
  ) => {
    if (
      (newWidth !== null && newWidth < CHAR_MIN_SIZE) ||
      (newHeight !== null && newHeight < CHAR_MIN_SIZE)
    ) {
      return;
    }

    const char = characters.find((char) => char.id === charId);
    if (!char) {
      throw new Error(`Unable to get character with id ${charId}!`);
    }
    updateCharacter({
      ...char,
      width: newWidth ?? char.width,
      height: newHeight ?? char.height,
      position: { x: newX ?? char.position.x, y: newY ?? char.position.y },
    });
  };

  const flipCharacter = (charId: string) => {
    const char = characters.find((char) => char.id === charId);
    if (!char) {
      throw new Error(`Unable to get character with id ${charId}!`);
    }

    let newOrientation;

    switch (char.orientation) {
      case CharacterOrientation.ORIGINAL:
        newOrientation = CharacterOrientation.FLIPPED;
        break;
      case CharacterOrientation.FLIPPED:
        newOrientation = CharacterOrientation.ORIGINAL;
        break;
      default:
        assertNever(char.orientation);
    }

    updateCharacter({
      ...char,
      orientation: newOrientation,
    });
  };

  const [characterInFocus, setCharacterInFocus] = useState<
    Character['id'] | null
  >(null);
  const clearCharacterFocus = () => {
    setCharacterInFocus(null);
  };

  ///////////////////////
  // SVG Container CTM //
  ///////////////////////
  const svgContainerRef = useRef<SVGSVGElement>(null);

  const [ctm, setCtm] = useState<DOMMatrix | null>(null);

  useEffect(() => {
    if (svgContainerRef.current) {
      setCtm(svgContainerRef.current.getScreenCTM());
    }
    // displayMode needs to stay in the dependency array as we need to calculate
    // a new CTM when the displayMode changes the size of the canvas
  }, [setCtm, displayMode]);

  const getContainerCTM = useCallback(() => {
    if (!ctm) {
      throw new Error('Unable to get SVG Container CTM!');
    }
    return ctm;
  }, [ctm]);

  return (
    <SceneCanvasContext.Provider
      value={{
        viewMode,
        displayMode,
        toggleDisplayMode,

        characters,
        getCharacterById,
        scaleCharacter,
        flipCharacter,

        characterInFocus,
        setCharacterInFocus,
        clearCharacterFocus,

        svg: {
          svgContainerRef,
          getContainerCTM,
          setContainerCTM: setCtm,
        },
      }}
    >
      {children}
    </SceneCanvasContext.Provider>
  );
};
